
import { LmvVector3 } from '../../../wgs/scene/LmvVector3';
import { LmvBox3 } from '../../../wgs/scene/LmvBox3';
import { LmvMatrix4 } from '../../../wgs/scene/LmvMatrix4';

export function getUnitScale(unit) {
  // Normalize to lowercase for consistent matching
  unit = unit?.toLowerCase();

  //Why are translators not using standard strings for those?!?!?!?
  switch (unit) {
    case 'meter':
    case 'meters':
    case 'm':return 1.0;
    case 'feet and inches':
    case 'foot':
    case 'feet':
    case 'ft':
    case 'international foot':
    case 'international feet':return 0.3048;
    case 'sft':
    case 'ft-us':
    case 'us survey foot':
    case 'us survey feet':
    case 'survey ft':return 1200 / 3937; // https://en.wikipedia.org/wiki/Foot_(unit)#Survey_foot
    case 'inch':
    case 'inches':
    case 'in':return 0.0254;
    case 'centimeter':
    case 'centimeters':
    case 'cm':return 0.01;
    case 'millimeter':
    case 'millimeters':
    case 'mm':return 0.001;
    case 'yard':
    case 'yards':
    case 'yd':return 0.9144;
    case 'kilometer':
    case 'kilometers':
    case 'km':return 1000.0;
    case 'mile':
    case 'miles':
    case 'mi':return 1609.344;
    default:return 1.0;
  }
}


function isIdentity(mtx) {
  var e = mtx.elements;
  for (var i = 0; i < 4; i++) {
    for (var j = 0; j < 4; j++) {
      if (i === j) {
        if (e[i * 4 + j] !== 1)
        return false;
      } else {
        if (e[i * 4 + j] !== 0)
        return false;
      }
    }
  }

  return true;
}

/**
 * Computes the world-up rotation and unit-scaling portions of the placement transform.
 * This is a pure function that can be called both at load time and at runtime to recompute
 * placement when up-direction or unit overrides change.
 *
 * @param {Object} params
 * @param {LmvMatrix4|null}    params.basePlacement                - Starting placement before up-rotation and scaling
 * @param {Object}             params.metadata                     - Model metadata object
 * @param {number[]|null}      [params.overrideModelUpDirection]   - Override for model's up direction [x,y,z]
 * @param {number[]|null}      [params.forceWorldUpDirection]      - Global up direction to align to [x,y,z]
 * @param {string|null}        [params.overrideDistanceUnit]       - Override for model's distance unit
 * @param {string|Object|null} [params.applyScaling]               - Scaling config (target unit string or {from, to})
 * @param {boolean}            [params.applyPlacementInModelUnits] - If true, base placement is applied in model units
 * @returns {{ placementTransform: LmvMatrix4|null, worldUpRotation: LmvMatrix4|null, scalingFactor: number|undefined, scalingUnit: string|undefined }}
 */
export function computeWorldUpAndScaling({
  basePlacement,
  metadata,
  overrideModelUpDirection,
  forceWorldUpDirection,
  overrideDistanceUnit,
  applyScaling,
  applyPlacementInModelUnits
}) {
  let runningPlacement = basePlacement;
  let worldUpRotation = null;
  let scalingFactor;
  let scalingUnit;

  if (forceWorldUpDirection) {
    const modelUpDirection = overrideModelUpDirection ||
    metadata["world up vector"]?.XYZ;
    if (modelUpDirection) {
      // Compute a rotation that aligns the model world up direction with the global world up
      const globalUp = new LmvVector3().fromArray(forceWorldUpDirection);
      const modelUp = new LmvVector3().fromArray(modelUpDirection);
      modelUp.normalize();

      // Check if the vectors are not already aligned
      const dot = modelUp.dot(globalUp);
      if (Math.abs(dot - 1.0) > 1e-6) {
        // Compute rotation matrix to align modelUp with globalUp
        const rotationMatrix = LmvMatrix4.getRotationMatrixAligningVectors(modelUp, globalUp);

        // Apply rotation to placement transform
        const placementTransform = new LmvMatrix4(true);
        if (runningPlacement) {
          // There may well already be a placementTransform from previous options/operations.
          placementTransform.copy(runningPlacement);
        }

        worldUpRotation = rotationMatrix.clone();

        if (applyPlacementInModelUnits) {
          // Apply placementMatrix first, then rotate the world up direction to the force world up direction.
          // In this way, the world up direction is rotated to the force world up direction before the placement is applied.
          runningPlacement = rotationMatrix.multiply(placementTransform);
        } else {
          // Apply rotation first, then placementTransform
          runningPlacement = placementTransform.multiply(rotationMatrix);
        }
      } else {
        worldUpRotation = new LmvMatrix4(true);
      }
    }
  }


  // If requested, apply scaling from optional 'from' to 'to' units.
  // If unspecified, then units will be read from the models metadata.
  // * usage overloads
  //      options.appyScaling: { from: 'ft', to: 'm' }
  //      options.appyScaling: 'm'   ( equivalent to { to: 'm' })
  // * this is aimed at multiple 3D model situations where models potentialy have different units, but
  //   one  doesn't up-front know what these units are.It also allows overriding of such units.
  // * Model methods: getUnitString , getUnitScale &  getDisplayUnit will be automatically return corrected values
  //   as long as there are no additional options.placementTransform scalings applied.
  if (applyScaling) {

    // default 'from' & 'to'  units are from metadata, or 'm' not present
    let scalingFromUnit = 'm';
    if (overrideDistanceUnit) {
      scalingFromUnit = overrideDistanceUnit;
    } else if (metadata["distance unit"]) {
      scalingFromUnit = metadata["distance unit"]["value"];
    }
    scalingUnit = scalingFromUnit;

    if ('object' === typeof applyScaling) {
      if (applyScaling.from) {
        scalingFromUnit = applyScaling.from;
      }
      if (applyScaling.to) {
        scalingUnit = applyScaling.to;
      }
    } else {
      scalingUnit = applyScaling;
    }

    // Work out overall desired scaling factor.
    const factor = getUnitScale(scalingFromUnit) / getUnitScale(scalingUnit);

    if (1 !== factor) {
      const placementS = new LmvMatrix4(true);
      const scalingTransform = new LmvMatrix4(true);
      scalingTransform.elements[0] = factor;
      scalingTransform.elements[5] = factor;
      scalingTransform.elements[10] = factor;

      if (runningPlacement) {
        // There may well already be a placementTransform from previous options/operations.
        placementS.copy(runningPlacement);
      }

      // Combine (optional) placementTransform with scalingTransform
      if (applyPlacementInModelUnits) {
        // Apply placementMatrix first, then scale to viewer world units. In this way,
        // the placementMatrix is handled in the same way as a refPointTransform.
        runningPlacement = scalingTransform.multiply(placementS);
      } else {
        // Apply scaling first, then placementMatrix
        runningPlacement = placementS.multiply(scalingTransform);
      }

      // Store scalingFactor, so that we know which unitScaling was applied to this model.
      // Multiplying with this factor converts model file units to viewer world units.
      scalingFactor = factor;
    }
  }

  return {
    placementTransform: runningPlacement,
    worldUpRotation,
    scalingFactor,
    scalingUnit
  };
}


export function derivePlacementTransform(svf, loadContext) {

  var custom_values = svf.metadata["custom values"];

  // We now will apply overall model transforms, following the following logic:
  //    1) placementTransform = options.placementTransform);
  //    2) placementTransform = placementTransform.multiply(scalingTransform);
  //    3) placementTransform = placementTransform.multiply(refPointTransform);
  // This is for aggregation scenarios, where multiple models are loaded into the scene
  // In such scenarios the client will most probably manually override the model units

  //First, take the input placement transform as is (could be null).
  svf.placementTransform = loadContext.placementTransform;

  // Store overrideModelUpDirection on svf so that Model.getUpVector() can use it
  // even when forceWorldUpDirection is not set.
  if (loadContext.overrideModelUpDirection) {
    svf.overrideModelUpDirection = loadContext.overrideModelUpDirection;
  }

  // Store overrideDistanceUnit on svf so that Model.getUnitString() can use it
  // even when applyScaling is not set.
  if (loadContext.overrideDistanceUnit) {
    svf.overrideDistanceUnit = loadContext.overrideDistanceUnit;
  }

  // Save the initial placement before world-up rotation and scaling modify it,
  // so we can recompute placement at runtime when overrides change.
  const inputPlacementTransform = loadContext.placementTransform ?
  new LmvMatrix4(true).copy(loadContext.placementTransform) :
  null;

  // Compute world-up rotation and unit scaling via the reusable pure function.
  const upAndScaling = computeWorldUpAndScaling({
    basePlacement: inputPlacementTransform,
    metadata: svf.metadata,
    overrideModelUpDirection: loadContext.overrideModelUpDirection,
    forceWorldUpDirection: loadContext.forceWorldUpDirection,
    overrideDistanceUnit: loadContext.overrideDistanceUnit,
    applyScaling: loadContext.applyScaling,
    applyPlacementInModelUnits: loadContext.applyPlacementInModelUnits
  });

  if (upAndScaling.worldUpRotation) {
    svf.worldUpRotation = upAndScaling.worldUpRotation;
  }
  if (upAndScaling.scalingUnit !== undefined) {
    svf.scalingUnit = upAndScaling.scalingUnit;
  }
  if (upAndScaling.scalingFactor !== undefined) {
    svf.scalingFactor = upAndScaling.scalingFactor;
  }
  if (upAndScaling.placementTransform) {
    svf.placementTransform = loadContext.placementTransform = upAndScaling.placementTransform;
  }

  // Store context for runtime recomputation of up-direction and scaling.
  svf.loadPlacementOptions = {
    inputPlacementTransform,
    forceWorldUpDirection: loadContext.forceWorldUpDirection,
    applyScaling: loadContext.applyScaling,
    applyRefPoint: loadContext.applyRefPoint,
    applyPlacementInModelUnits: loadContext.applyPlacementInModelUnits
  };

  if (custom_values && custom_values.refPointTransform) {

    svf.refPointTransform = new LmvMatrix4(true);
    var e = svf.refPointTransform.elements;
    var src = custom_values.refPointTransform;

    e[0] = src[0];
    e[1] = src[1];
    e[2] = src[2];

    e[4] = src[3];
    e[5] = src[4];
    e[6] = src[5];

    e[8] = src[6];
    e[9] = src[7];
    e[10] = src[8];

    e[12] = src[9];
    e[13] = src[10];
    e[14] = src[11];

  } else {
    //Is there an extra offset specified in the georeference?
    //This is important when aggregating Revit models from the same Revit
    //project into the same scene, because Revit SVFs use RVT internal coordinates, which
    //need extra offset to get into the world space.
    var georeference = svf.metadata["georeference"];
    var refPointLMV = georeference && georeference["refPointLMV"];

    var angle = 0;
    if (custom_values && Object.prototype.hasOwnProperty.call(custom_values, "angleToTrueNorth")) {
      angle = Math.PI / 180.0 * custom_values["angleToTrueNorth"];
    }

    if (refPointLMV || angle) {

      var rotation = new LmvMatrix4(true);
      var m = rotation.elements;
      m[0] = m[5] = Math.cos(angle);
      m[1] = -Math.sin(angle);
      m[4] = Math.sin(angle);

      //refPointLMV is given in model local coordinates, hence the negation needed
      //to make the translation go from local to shared coordinates.
      var offset = new LmvMatrix4(true);
      m = offset.elements;
      if (refPointLMV) {
        m[12] = -refPointLMV[0];
        m[13] = -refPointLMV[1];
        m[14] = -refPointLMV[2];
      }

      //Compose the rotation and offset.
      svf.refPointTransform = rotation.multiply(offset);
    }
  }

  //If request in the load options, apply the reference point transform when loading the model
  if (loadContext.applyRefPoint && svf.refPointTransform) {

    var placement = new LmvMatrix4(true);

    //Normally we expect the input placement transform to come in as identity in case
    //we have it specified in the georef here, but, whatever, let's be thorough for once.
    if (loadContext.placementTransform)
    placement.copy(loadContext.placementTransform);

    placement.multiply(svf.refPointTransform);

    svf.placementTransform = loadContext.placementTransform = placement;

  } else if (!loadContext.applyRefPoint && loadContext.placementTransform) {

    //In case we are given a placement transform that overrides the incoming refPointTransform
    svf.placementTransform = new LmvMatrix4(true).copy(loadContext.placementTransform);
  }

  if (svf.placementTransform && isIdentity(svf.placementTransform))
  svf.placementTransform = null;

  return svf.placementTransform;
}

export function calculatePlacementWithOffset(svf, pt) {
  var go = svf.globalOffset;
  if (go.x || go.y || go.z) {
    if (!pt) {
      pt = new LmvMatrix4(true);
      pt.makeTranslation(-go.x, -go.y, -go.z);
    } else {
      var pt2 = new LmvMatrix4(true);
      pt2.copy(pt);
      pt = pt2;
      pt.elements[12] -= go.x;
      pt.elements[13] -= go.y;
      pt.elements[14] -= go.z;
    }

    svf.placementWithOffset = pt;
  } else {
    svf.placementWithOffset = pt;
  }
}

export function initPlacement(svf, loadContext) {

  if (!svf.metadata)
  return;

  //Retrieve world bounding box
  var bbox = svf.metadata["world bounding box"];
  var min = new LmvVector3(bbox.minXYZ[0], bbox.minXYZ[1], bbox.minXYZ[2]);
  var max = new LmvVector3(bbox.maxXYZ[0], bbox.maxXYZ[1], bbox.maxXYZ[2]);
  svf.bbox = new LmvBox3(min, max);
  svf.modelSpaceBBox = svf.bbox.clone();

  var pt = derivePlacementTransform(svf, loadContext);
  if (pt && !svf.bbox.isEmpty()) {
    svf.bbox.applyMatrix4(pt);
  }

  //Global offset is used to avoid floating point precision issues for models
  //located enormous distances from the origin. The default is to move the model to the origin
  //but it can be overridden in case of model aggregation scenarios, where multiple
  //models are loaded into the scene and a common offset is needed for all.
  if (loadContext.globalOffset) {
    // Apply user-defined globalOffset
    svf.globalOffset = loadContext.globalOffset;
  } else {
    // Choose global offset automatically at the center of the placmenent transformed model.
    svf.globalOffset = svf.bbox.getCenter(new LmvVector3());
  }

  calculatePlacementWithOffset(svf, pt);

  // The model boundingBox must finally be in viewer-coords, just like everything else. I.e. with subtracted offset.
  // Therefore, we have to subtract the globaloffset from bbox as well.
  if (!svf.bbox.isEmpty()) {
    svf.bbox.min.sub(svf.globalOffset);
    svf.bbox.max.sub(svf.globalOffset);
  }

  if (Object.prototype.hasOwnProperty.call(svf.metadata, "double sided geometry") &&
  svf.metadata["double sided geometry"]["value"]) //TODO: do we want to check the global flag or drop that and rely on material only?
    {
      svf.doubleSided = true;
    }

}

function applyOffset(a, offset) {
  a[0] -= offset.x;
  a[1] -= offset.y;
  a[2] -= offset.z;
}


export function transformAnimations(svf) {

  if (!svf.animations)
  return;

  // apply global offset to animations
  var animations = svf.animations["animations"];
  if (animations) {
    var globalOffset = svf.globalOffset;
    var t = new LmvMatrix4().makeTranslation(globalOffset.x, globalOffset.y, globalOffset.z);
    var tinv = new LmvMatrix4().makeTranslation(-globalOffset.x, -globalOffset.y, -globalOffset.z);
    var r = new LmvMatrix4();
    var m = new LmvMatrix4();
    for (var a = 0; a < animations.length; a++) {
      var anim = animations[a];
      if (anim.hierarchy) {
        for (var h = 0; h < anim.hierarchy.length; h++) {
          var keys = anim.hierarchy[h].keys;
          if (keys) {
            for (var k = 0; k < keys.length; k++) {
              var pos = keys[k].pos;
              if (pos) {
                var offset = globalOffset;
                var rot = keys[k].rot;
                if (rot) {
                  r.makeRotationFromQuaternion({ x: rot[0], y: rot[1], z: rot[2], w: rot[3] });
                  m.multiplyMatrices(t, r).multiply(tinv);
                  offset = { x: m.elements[12], y: m.elements[13], z: m.elements[14] };
                }
                applyOffset(pos, offset);
              }
              var target = keys[k].target;
              if (target) {
                applyOffset(target, globalOffset);
              }
              var points = keys[k].points;
              if (points) {
                for (var p = 0; p < points.length; p++) {
                  applyOffset(points[p], globalOffset);
                }
              }
            }
          }
        }
      }
    }
  }
}

// Apply transform (if any) to given camera data with { position, target, up, orthoScale }
export function transformCameraData(cam, transform) {

  if (!transform) {
    return;
  }

  transform.transformPoint(cam.position);
  transform.transformPoint(cam.target);
  transform.transformDirection(cam.up);

  // If the placement includes scaling (either by unit scaling or manually included
  // in the placement transform), orthoScale values need to be scaled as well.
  //
  // Note: We don't support non-uniform scaling well. However, home-views cannot be recovered
  //       anyway if the model is distorted with non-uniform transforms.
  if (isFinite(cam.orthoScale)) {
    const scale = transform.getMaxScaleOnAxis();
    cam.orthoScale *= scale;
  }
}